import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  LinearProgress,
  Button,
  IconButton,
  Tooltip as MuiTooltip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BuildIcon from '@mui/icons-material/Build';
import DownloadIcon from '@mui/icons-material/Download';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { format, subDays } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import { useIsMobile } from '@/hooks/useIsMobile';
import {
  useRevenueReport,
  useReportSummary,
  useMechanicPerformance,
  useServiceTypeStats,
  useMonthlyComparison,
  useCustomerStats,
  useCustomerSatisfaction,
  useAgentPerformance,
  useWorkOrderDurationPerformance,
} from '@/hooks';
import { useNotify } from '@/stores/notificationStore';
import { UserAvatar } from '@/components/common';
import { chartColors } from '@/styles/theme';
import { chartTooltipProps } from '@/lib/chartTheme';
import { LiftUtilizationReport } from '@/components/reports/LiftUtilizationReport';
import { ParkingUtilizationReport } from '@/components/reports/ParkingUtilizationReport';
import { CustomerSatisfactionSection } from './sections/CustomerSatisfactionSection';
import { AgentPerformanceSection } from './sections/AgentPerformanceSection';
import { ReportSectionStatus } from './sections/ReportSectionShell';
import { EMPTY_AGENT_PERFORMANCE, EMPTY_SATISFACTION } from './reportMeasurements';
import {
  DurationPerformanceSection,
  formatDurationRatioPercent,
} from './sections/DurationPerformanceSection';
import { downloadCsv } from '@/utils/csv';
import { dateFnsLocale } from '@/utils/locale';

type DateRange = '7d' | '30d' | '90d' | '12m';
type ChartView = 'revenue' | 'workorders';

const COLORS = [...chartColors];
const PIE_MAX_SLICES = 6;

export default function ReportsPage() {
  const { t } = useTranslation('reports');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const theme = useTheme();

  const [dateRange, setDateRange] = useState<DateRange>('30d');
  const [chartView, setChartView] = useState<ChartView>('revenue');

  // Compute date range strings for API calls
  const { dateFrom, dateTo, groupBy } = useMemo(() => {
    const now = new Date();
    const days = dateRange === '7d' ? 7 : dateRange === '30d' ? 30 : dateRange === '90d' ? 90 : 365;
    const from = subDays(now, days);
    return {
      dateFrom: format(from, 'yyyy-MM-dd'),
      dateTo: format(now, 'yyyy-MM-dd'),
      groupBy: dateRange === '12m' ? 'month' : dateRange === '90d' ? 'week' : 'day',
    };
  }, [dateRange]);

  // API hooks
  const revenueQuery = useRevenueReport(dateFrom, dateTo, groupBy);
  const summaryQuery = useReportSummary(dateFrom, dateTo);
  const mechanicQuery = useMechanicPerformance(dateFrom, dateTo);
  const serviceTypeQuery = useServiceTypeStats(dateFrom, dateTo);
  const monthlyQuery = useMonthlyComparison();
  const customerQuery = useCustomerStats(dateFrom, dateTo);

  // Derived data with defaults
  const revenueData = revenueQuery.data?.data ?? [];
  const mechanicProductivity = useMemo(() =>
    (mechanicQuery.data?.data ?? []).map((m) => ({
      mechanicId: m.mechanicId,
      mechanicName: m.name,
      workOrdersCompleted: m.workOrders,
      totalRevenue: m.revenue,
      laborHours: m.laborHours,
      efficiency: m.efficiency,
      billableEfficiency: m.billableEfficiency ?? null,
      billableEfficiencyTasks: m.billableEfficiencyTasks ?? null,
    })),
  [mechanicQuery.data]);
  const serviceTypeStats = useMemo(() =>
    (serviceTypeQuery.data?.data ?? []).map((s) => ({
      serviceType: s.name,
      count: s.count,
      revenue: s.revenue,
      avgDuration: s.avgDuration,
    })).sort((a, b) => b.count - a.count),
  [serviceTypeQuery.data]);
  // Free-text service names make a long tail; the pie keeps the largest slices and folds the rest
  const serviceDistribution = useMemo(() => {
    const total = serviceTypeStats.reduce((sum, s) => sum + s.count, 0);
    const pct = (count: number) => (total > 0 ? Math.round((count / total) * 100) : 0);
    const slices = serviceTypeStats.slice(0, PIE_MAX_SLICES).map((s, index) => ({
      name: s.serviceType,
      count: s.count,
      pct: pct(s.count),
      color: COLORS[index % COLORS.length] as string,
    }));
    const restCount = serviceTypeStats.slice(PIE_MAX_SLICES).reduce((sum, s) => sum + s.count, 0);
    if (restCount > 0) {
      slices.push({ name: t('otherServices'), count: restCount, pct: pct(restCount), color: theme.palette.text.disabled });
    }
    return slices;
  }, [serviceTypeStats, t, theme.palette.text.disabled]);
  const customerStats = customerQuery.data ?? {
    newCustomers: 0,
    returningCustomers: 0,
    totalCustomers: 0,
    avgVisitsPerCustomer: 0,
    topCustomers: [],
  };
  const dashboardStats = summaryQuery.data ?? {
    totalRevenue: 0,
    revenueChange: 0,
    workOrdersCompleted: 0,
    workOrdersChange: 0,
    avgTicketValue: 0,
    avgTicketChange: 0,
    laborHours: 0,
    laborHoursChange: 0,
  };
  const monthlyComparison = monthlyQuery.data?.data ?? [];

  // Customer satisfaction — real data from booking_ratings (#973 A3/A4)
  const satisfactionQuery = useCustomerSatisfaction(dateFrom, dateTo, groupBy);
  // Placeholders are "not measured" (null), never 0; loading/error get their own state below.
  const satisfactionData = satisfactionQuery.data ?? EMPTY_SATISFACTION;
  const satisfactionTrend = satisfactionQuery.data?.trend ?? [];

  // Agent performance — real data from agent analytics (#973 B2/B3)
  const agentPerformanceQuery = useAgentPerformance(dateFrom, dateTo, groupBy);
  const durationPerformanceQuery = useWorkOrderDurationPerformance(dateFrom, dateTo);
  const agentPerformanceData = agentPerformanceQuery.data ?? EMPTY_AGENT_PERFORMANCE;
  const agentPerformanceTrend = agentPerformanceQuery.data?.trend ?? [];

  const formatPrice = (cents: number) => `€${(cents / 100).toLocaleString('fi-FI', { minimumFractionDigits: 0 })}`;
  const formatPriceShort = (cents: number) => {
    if (cents >= 100000000) return `€${(cents / 100000000).toFixed(1)}M`;
    if (cents >= 100000) return `€${(cents / 100000).toFixed(0)}k`;
    return `€${(cents / 100).toFixed(0)}`;
  };

  /**
   * Export the revenue series actually on screen (#2712).
   *
   * This used to be a lone `notify.info` promising a download that never
   * came. There is no export endpoint and no saved-report storage, so the
   * file is built from the data already loaded rather than by adding a
   * backend route.
   *
   * Cents are converted to euros here: the CSV is for a human in Excel, and
   * the integer-cents rule governs storage and arithmetic, not display.
   */
  const handleExport = () => {
    if (revenueData.length === 0) {
      notify.warning(t('exportEmpty'), t('exportEmptyDesc'));
      return;
    }
    downloadCsv(
      `${t('exportFilename')}-${dateFrom}-${dateTo}`,
      [tc('date'), t('revenue'), t('labor'), t('parts'), t('otherRevenue'), t('workOrders')],
      revenueData.map((row) => [
        row.date,
        row.revenue / 100,
        row.labor / 100,
        row.parts / 100,
        row.other / 100,
        row.workOrders,
      ]),
    );
    notify.success(t('exportReady'), t('exportReadyDesc', { count: revenueData.length }));
  };

  const StatCard = ({
    title,
    value,
    change,
    icon,
  }: {
    title: string;
    value: string;
    change: number;
    icon: React.ReactNode;
  }) => (
    <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: 'divider', height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="caption" color="text.secondary">
            {title}
          </Typography>
          <Typography variant="h5" fontWeight={600}>
            {value}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
            {change >= 0 ? (
              <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main' }} />
            ) : (
              <TrendingDownIcon sx={{ fontSize: 16, color: 'error.main' }} />
            )}
            <Typography variant="caption" color={change >= 0 ? 'success.main' : 'error.main'}>
              {change >= 0 ? '+' : ''}
              {change.toFixed(1)}%
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('vsPrevious')}
            </Typography>
          </Box>
        </Box>
        <Avatar sx={{ bgcolor: 'primary.light', width: 40, height: 40 }}>{icon}</Avatar>
      </Box>
    </Paper>
  );

  return (
    <Box sx={{ overflow: 'hidden', maxWidth: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" fontWeight={600}>
          {t('title')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>{t('dateRange')}</InputLabel>
            <Select
              value={dateRange}
              label={t('dateRange')}
              onChange={(e) => setDateRange(e.target.value as DateRange)}
            >
              <MenuItem value="7d">{t('last7Days')}</MenuItem>
              <MenuItem value="30d">{t('last30Days')}</MenuItem>
              <MenuItem value="90d">{t('last90Days')}</MenuItem>
              <MenuItem value="12m">{t('last12Months')}</MenuItem>
            </Select>
          </FormControl>
          {isMobile ? (
            <MuiTooltip title={t('createReport')}>
              <IconButton color="primary" onClick={() => navigate('/reports/builder')} aria-label={t('createReport')}>
                <AddIcon />
              </IconButton>
            </MuiTooltip>
          ) : (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/reports/builder')}
            >
              {t('createCustomReport')}
            </Button>
          )}
          <Button variant="outlined" size={isMobile ? 'small' : 'medium'} startIcon={<DownloadIcon />} onClick={handleExport}>
            {t('export')}
          </Button>
        </Box>
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            title={t('totalRevenue')}
            value={formatPrice(dashboardStats.totalRevenue)}
            change={dashboardStats.revenueChange}
            icon={<TrendingUpIcon />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            title={t('workOrdersCompleted')}
            value={dashboardStats.workOrdersCompleted.toString()}
            change={dashboardStats.workOrdersChange}
            icon={<ReceiptIcon />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            title={t('avgTicketValue')}
            value={formatPrice(dashboardStats.avgTicketValue)}
            change={dashboardStats.avgTicketChange}
            icon={<BuildIcon />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            title={t('laborHours')}
            value={`${dashboardStats.laborHours}h`}
            change={dashboardStats.laborHoursChange}
            icon={<AccessTimeIcon />}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Revenue Chart */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, border: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="h6" fontWeight={600}>
                {t('revenueOverTime')}
              </Typography>
              <ToggleButtonGroup
                value={chartView}
                exclusive
                onChange={(_, v) => v && setChartView(v)}
                size="small"
              >
                <ToggleButton value="revenue">{t('revenue')}</ToggleButton>
                <ToggleButton value="workorders">{t('workOrders')}</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            {revenueData.length === 0 ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: isMobile ? 200 : 300 }}>
                <Typography color="text.secondary">{tc('noData')}</Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={isMobile ? 200 : 300}>
                {chartView === 'revenue' ? (
                  <AreaChart data={revenueData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(date) => format(new Date(date), 'd.M', { locale: dateFnsLocale() })}
                    />
                    <YAxis tickFormatter={(v) => formatPriceShort(v)} />
                    <Tooltip
                      {...chartTooltipProps(theme)}
                      formatter={(value: number) => formatPrice(value)}
                      labelFormatter={(date) => format(new Date(date), 'd.M.yyyy', { locale: dateFnsLocale() })}
                    />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="labor"
                      stackId="1"
                      stroke={theme.palette.primary.main}
                      fill={theme.palette.primary.main}
                      name={t('labor')}
                    />
                    <Area
                      type="monotone"
                      dataKey="parts"
                      stackId="1"
                      stroke={theme.palette.success.main}
                      fill={theme.palette.success.main}
                      name={t('parts')}
                    />
                    <Area
                      type="monotone"
                      dataKey="other"
                      stackId="1"
                      stroke={theme.palette.warning.main}
                      fill={theme.palette.warning.main}
                      name={t('otherRevenue')}
                    />
                  </AreaChart>
                ) : (
                  <LineChart data={revenueData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(date) => format(new Date(date), 'd.M', { locale: dateFnsLocale() })}
                    />
                    <YAxis />
                    <Tooltip
                      {...chartTooltipProps(theme)}
                      labelFormatter={(date) => format(new Date(date), 'd.M.yyyy', { locale: dateFnsLocale() })}
                    />
                    <Line
                      type="monotone"
                      dataKey="workOrders"
                      stroke={theme.palette.primary.main}
                      strokeWidth={2}
                      dot={{ fill: theme.palette.primary.main }}
                      name={t('workOrders')}
                    />
                  </LineChart>
                )}
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>

        {/* Service Type Distribution */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, border: 1, borderColor: 'divider', height: '100%' }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              {t('serviceDistribution')}
            </Typography>
            {serviceTypeStats.length === 0 ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: isMobile ? 180 : 260 }}>
                <Typography color="text.secondary">{tc('noData')}</Typography>
              </Box>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={isMobile ? 180 : 220}>
                  <PieChart>
                    <Pie
                      data={serviceDistribution}
                      dataKey="count"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={isMobile ? 70 : 90}
                    >
                      {serviceDistribution.map((slice) => (
                        <Cell key={slice.name} fill={slice.color} />
                      ))}
                    </Pie>
                    <Tooltip {...chartTooltipProps(theme)} formatter={(value: number) => `${value} ${t('orders')}`} />
                  </PieChart>
                </ResponsiveContainer>
                <Box component="ul" data-testid="service-distribution-legend" sx={{ listStyle: 'none', m: 0, mt: 1, p: 0 }}>
                  {serviceDistribution.map((slice) => (
                    <Box
                      component="li"
                      key={slice.name}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25, minWidth: 0 }}
                    >
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: slice.color, flexShrink: 0 }} />
                      <MuiTooltip title={slice.name} placement="top-start">
                        <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
                          {slice.name}
                        </Typography>
                      </MuiTooltip>
                      <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                        {slice.pct} %
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </Paper>
        </Grid>

        {/* Monthly Comparison */}
        <Grid size={{ xs: 12, lg: 6 }}>
          <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, border: 1, borderColor: 'divider' }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              {t('yearlyComparison')}
            </Typography>
            {monthlyComparison.length === 0 ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: isMobile ? 200 : 280 }}>
                <Typography color="text.secondary">{tc('noData')}</Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={isMobile ? 200 : 280}>
                <BarChart data={monthlyComparison}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis tickFormatter={(v) => formatPriceShort(v)} />
                  <Tooltip {...chartTooltipProps(theme)} formatter={(value: number) => formatPrice(value)} />
                  <Legend />
                  <Bar dataKey="current" fill={theme.palette.primary.main} name={t('thisYear')} />
                  <Bar dataKey="previous" fill={theme.palette.text.secondary} name={t('lastYear')} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>

        {/* Mechanic Productivity */}
        <Grid size={{ xs: 12, lg: 6 }}>
          <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, border: 1, borderColor: 'divider' }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              {t('mechanicProductivity')}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1 }}>
              {t('workdayUtilizationHint')}
            </Typography>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('mechanic')}</TableCell>
                    <TableCell align="right">{t('workOrders')}</TableCell>
                    <TableCell align="right">{t('revenue')}</TableCell>
                    <TableCell align="right">{t('hours')}</TableCell>
                    <TableCell>{t('workdayUtilization')}</TableCell>
                    <TableCell align="right">{t('billableEfficiencyPercent')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mechanicProductivity.map((mech) => (
                    <TableRow key={mech.mechanicId} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <UserAvatar
                            name={mech.mechanicName}
                            size={28}
                          />
                          <Typography variant="body2">{mech.mechanicName}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={500}>
                          {mech.workOrdersCompleted}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={500}>
                          {formatPrice(mech.totalRevenue)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">{mech.laborHours}h</Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={mech.efficiency}
                            sx={{ flex: 1, height: 6, borderRadius: 1 }}
                            color="primary"
                          />
                          <Typography variant="caption" sx={{ minWidth: 35 }}>
                            {mech.efficiency}%
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" component="span">
                          {formatDurationRatioPercent(mech.billableEfficiency)}
                        </Typography>
                        {mech.billableEfficiencyTasks != null && (
                          <Typography variant="caption" color="text.secondary" component="div">
                            {t('cohortTasks', { count: mech.billableEfficiencyTasks })}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {(mechanicQuery.data?.unattributedRevenue ?? 0) !== 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {t('unattributedMechanic', { amount: formatPrice(mechanicQuery.data?.unattributedRevenue ?? 0) })}
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Service Type Revenue */}
        <Grid size={{ xs: 12, lg: 6 }}>
          <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, border: 1, borderColor: 'divider' }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              {t('revenueByService')}
            </Typography>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('serviceType')}</TableCell>
                    <TableCell align="right">{t('count')}</TableCell>
                    <TableCell align="right">{t('revenue')}</TableCell>
                    <TableCell align="right">{t('avgDuration')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {serviceTypeStats.map((service, index) => (
                    <TableRow key={service.serviceType} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              bgcolor: COLORS[index % COLORS.length],
                            }}
                          />
                          <Typography variant="body2">{service.serviceType}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Chip label={service.count} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={500}>
                          {formatPrice(service.revenue)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">{service.avgDuration} h</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {(serviceTypeQuery.data?.unattributedRevenue ?? 0) !== 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {t('unattributedService', { amount: formatPrice(serviceTypeQuery.data?.unattributedRevenue ?? 0) })}
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Customer Stats */}
        <Grid size={{ xs: 12, lg: 6 }}>
          <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, border: 1, borderColor: 'divider' }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              {t('customerStats')}
            </Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('newCustomers')}
                </Typography>
                <Typography variant="h6" fontWeight={600} color="success.main">
                  +{customerStats.newCustomers}
                </Typography>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('returningCustomers')}
                </Typography>
                <Typography variant="h6" fontWeight={600}>
                  {customerStats.returningCustomers}
                </Typography>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('totalCustomers')}
                </Typography>
                <Typography variant="h6" fontWeight={600}>
                  {customerStats.totalCustomers}
                </Typography>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('avgVisits')}
                </Typography>
                <Typography variant="h6" fontWeight={600}>
                  {customerStats.avgVisitsPerCustomer.toFixed(1)}
                </Typography>
              </Grid>
            </Grid>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
              {t('topCustomers')}
            </Typography>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('customer')}</TableCell>
                    <TableCell align="right">{t('revenue')}</TableCell>
                    <TableCell align="right">{t('visits')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {customerStats.topCustomers.map((customer, index) => {
                    // fixed gold/silver/bronze medal rank colors, not themeable
                    const medalColors = ['#FFD700', '#C0C0C0', '#CD7F32']; // design-allow-hex
                    const isMedal = index < 3;
                    return (
                    <TableRow key={customer.name} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar
                            sx={{
                              width: 24,
                              height: 24,
                              fontSize: 12,
                              bgcolor: isMedal ? medalColors[index] : 'primary.light',
                              color: isMedal ? 'common.black' : undefined,
                              fontWeight: isMedal ? 700 : undefined,
                            }}
                          >
                            {index + 1}
                          </Avatar>
                          <Typography variant="body2">{customer.name}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={500}>
                          {formatPrice(customer.revenue)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Chip label={customer.visits} size="small" variant="outlined" />
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {(customerStats.unattributedRevenue ?? 0) !== 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {t('unattributedCustomer', { amount: formatPrice(customerStats.unattributedRevenue ?? 0) })}
              </Typography>
            )}
          </Paper>
        </Grid>

        {satisfactionQuery.data === undefined && (satisfactionQuery.isLoading || satisfactionQuery.isError) ? (
          <ReportSectionStatus
            title={t('customerSatisfaction')}
            testId="customer-satisfaction-section"
            isLoading={satisfactionQuery.isLoading}
            isError={satisfactionQuery.isError}
          />
        ) : (
          <CustomerSatisfactionSection
            satisfactionData={satisfactionData}
            satisfactionTrend={satisfactionTrend}
            isMobile={isMobile}
          />
        )}

        {agentPerformanceQuery.data === undefined && (agentPerformanceQuery.isLoading || agentPerformanceQuery.isError) ? (
          <ReportSectionStatus
            title={t('agentPerformance')}
            testId="agent-performance-section"
            isLoading={agentPerformanceQuery.isLoading}
            isError={agentPerformanceQuery.isError}
          />
        ) : (
          <AgentPerformanceSection
            agentPerformanceData={agentPerformanceData}
            agentPerformanceTrend={agentPerformanceTrend}
            isMobile={isMobile}
            colors={COLORS}
          />
        )}

        <DurationPerformanceSection query={durationPerformanceQuery} />
      </Grid>

      {/* Lift Utilization Report */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <LiftUtilizationReport dateFrom={dateFrom} dateTo={dateTo} />
      </Paper>

      {/* Parking Utilization Report */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <ParkingUtilizationReport />
      </Paper>
    </Box>
  );
}
